import type { OnboardingStep } from './types.js';

/**
 * The wizard's CHAPTER model.
 *
 * The owner used to walk nine counted screens. They now open five chapters, in
 * any order, from one card stack — each chapter is a bottom sheet that closes
 * back to its card carrying a green or amber mark. This module is the pure
 * half of that: which questions live in which chapter, and whether a chapter
 * is done. It imports nothing but types, so it is unit-testable on its own and
 * the route cannot quietly grow a second opinion about either.
 *
 * WHY GROUPING RATHER THAN A SHORTER FLOW: no question was dropped in the
 * merge (with one deliberate exception, see `team-size` below) — the nine
 * section ids all survive as SECTIONS, and only their grouping into screens
 * changed. That is what keeps a resumed `draft.step` valid without a draft
 * version bump, and it is what `flatten(CHAPTER_SECTIONS) === SECTION_ORDER`
 * in the tests exists to guard: a section silently deleted during a future
 * edit fails that assertion rather than shipping as a question the owner is
 * never asked.
 */
export type Chapter = 'identity' | 'offering' | 'team' | 'hours' | 'storefront';

/**
 * The five chapters, in the order the stack lists them. This order is the one
 * thing `docs/decisions/0028-*.md` got right and that survives the redesign —
 * who you are, what you sell, who does it, when, and how it looks. Free
 * navigation means the owner may answer them in any order; this is the order
 * they are OFFERED in, which is still a real decision.
 */
export const CHAPTER_ORDER: readonly Chapter[] = [
  'identity',
  'offering',
  'team',
  'hours',
  'storefront',
] as const;

/**
 * Which sections each chapter asks. The section ids are the OLD step names,
 * kept verbatim on purpose — a draft persisted by the previous wizard stores
 * one of them in `draft.step`, and `chapterOf` is what turns that into a
 * chapter to reopen. Renaming them would strand every in-flight draft.
 */
export const CHAPTER_SECTIONS: Record<Chapter, readonly OnboardingStep[]> = {
  identity: ['basics', 'category'],
  offering: ['services'],
  team: ['staff'],
  hours: ['staff-hours'],
  // `contacts` (the address-book opt-in) sits here rather than in its own
  // chapter because this chapter is already "how customers reach and see
  // you" — and the business PHONE moved here from `basics` for the same
  // reason, plus a measured one: with the phone field still on it, the
  // identity sheet pushed the trade grid — the most important control on the
  // first screen an owner ever opens — below the fold.
  storefront: ['description', 'photo', 'contacts'],
};

/**
 * Every section a chapter can ask, in stack order. Derived rather than
 * restated so the two can never disagree; the test asserts it against an
 * explicit literal, which is the assertion that actually catches a dropped
 * question.
 */
export const SECTION_ORDER: readonly OnboardingStep[] = CHAPTER_ORDER.flatMap(
  (chapter) => CHAPTER_SECTIONS[chapter],
);

/**
 * A chapter id in the PascalCase form its translation keys are spelled in:
 * `chapter${chapterKey(c)}Title` / `…Prompt`. Three files were computing this
 * same `charAt(0).toUpperCase()` inline, and the welcome screen's chapter list
 * would have been a fourth — it lives here so the key shape is stated once,
 * next to the ids it is derived from.
 */
export function chapterKey(chapter: Chapter): string {
  return chapter.charAt(0).toUpperCase() + chapter.slice(1);
}

/**
 * Sections that exist in `OnboardingStep` but belong to NO chapter.
 *
 * - `welcome` and `review` were always outside the count.
 * - `team-size` is the one question the redesign genuinely deletes (owner,
 *   2026-08-11). Its three answers were never persisted anywhere — they only
 *   branched which staff form rendered next — so asking it cost a screen and
 *   bought nothing. The `team` chapter now opens with the owner already listed
 *   and an add button. The id stays in the union only so a draft saved mid-flow
 *   by the old wizard still resolves; see `chapterOf`.
 */
const UNCHAPTERED: readonly OnboardingStep[] = ['welcome', 'review', 'team-size'] as const;

/**
 * Which chapter to open for a section id — used when resuming a draft saved by
 * the previous wizard, which stores a step name rather than a chapter.
 *
 * `team-size` maps to `team` rather than to `null`: it is a deleted QUESTION,
 * not a deleted TOPIC, and an owner who quit on it was in the middle of
 * telling us about their team. `welcome`/`review` return null — the stack
 * itself is where both of those now land.
 */
export function chapterOf(step: OnboardingStep): Chapter | null {
  if (step === 'team-size') return 'team';
  if (UNCHAPTERED.includes(step)) return null;
  return CHAPTER_ORDER.find((c) => CHAPTER_SECTIONS[c].includes(step)) ?? null;
}

/**
 * Everything a chapter's completeness depends on, as a flat snapshot. A plain
 * value rather than the route's ~40 pieces of state so the rules below stay
 * pure and testable; the route builds one of these per render.
 */
export type WizardFacts = {
  /** Mandatory since 2026-08-12, and the ONE rule the client enforces that the
   *  server has no key for - see `chapterMissing`. */
  bizName: string;
  address: string;
  /**
   * Whether a Places pick backs `address` - i.e. the route holds coordinates
   * for the text currently in the field.
   *
   * MANDATORY, NOT COSMETIC, and this is the whole reason it exists as a
   * separate fact. `finishWizard` sends `address`/`lat`/`lng` as one bundle
   * and ONLY when a pick backs the text, which is the app-wide rule (see
   * `business-profile-settings.tsx`'s `saveContact`: the stored address and
   * its map pin must always agree, so free-typed text never overwrites a good
   * pair). Counting a hand-typed address as complete therefore greenlit a
   * submit that silently dropped it: five green cards, an enabled
   * `שליחה לאישור`, and a business whose `address` column stayed NULL - so
   * `checkPublishReadiness` reported `address` missing the moment the owner
   * landed on their dashboard, which is exactly the "i already finished" they
   * reported on 2026-08-13.
   */
  hasAddressPin: boolean;
  /** Translated trade labels, main first. Empty ⇒ no trade picked. */
  categories: readonly string[];
  serviceCount: number;
  /** People added, regardless of whether they have hours yet. */
  staffCount: number;
  /** Of those, how many have at least one open day. */
  staffWithHoursCount: number;
  /** The BUSINESS phone (moved to `storefront`), not the owner's login phone. */
  phone: string;
  hasPhoto: boolean;
  /**
   * Whether a cancellation window has been picked. MANDATORY, and the newest
   * of the storefront chapter's three (owner, 2026-08-13).
   *
   * `applyProfileUpdate`'s publish gate refuses `isPublished: true` while the
   * column is null, independently of approval and of readiness - so a wizard
   * that did not ask for one produced a business that could clear platform
   * review and still not go live, with biz-home's `פרסום` button quietly
   * rerouting to `business-advanced-settings` instead of publishing. The
   * wizard asks now, which is what makes finishing it sufficient.
   *
   * A BOOLEAN, not the number: 0 is a valid answer (`גמיש`, cancel any time)
   * and satisfies the gate exactly as 1440 does, so completeness is "picked or
   * not" and can never be confused with falsiness.
   */
  hasCancellationWindow: boolean;
  /** Optional questions. They never block submit, but they are evidence the
   *  owner has been in the chapter that asks them. */
  hasDescription: boolean;
  importContacts: boolean;
};

/**
 * Which chapters carry an answer of any kind — mandatory or optional.
 *
 * This is how `visited` survives a restart. The alternative was to persist the
 * set of opened chapters and nothing else, which gets one important case
 * wrong: an owner who fills in the whole identity chapter, closes the app and
 * comes back would see a blank, unmarked card over completed work, and a
 * submit button that opens while all five cards look untouched. Deriving from
 * the answers makes the resumed case right with no new state at all.
 *
 * The route still UNIONS this with a persisted set, because there is one thing
 * evidence cannot show: a chapter opened and closed without entering anything.
 * That deserves its amber mark ("you have been here and it is still empty"),
 * and only a recorded visit can say so.
 */
export function chaptersWithAnyAnswer(facts: WizardFacts): readonly Chapter[] {
  const answered: Record<Chapter, boolean> = {
    identity:
      facts.bizName.trim() !== '' || facts.address.trim() !== '' || facts.categories.length > 0,
    offering: facts.serviceCount > 0,
    team: facts.staffCount > 0,
    // NOT `staffWithHoursCount > 0`, which it was until 2026-08-12. A staff
    // member is now created with a prefilled Sun-Thu week
    // (`hours.ts:defaultDayHours`), so that test is true the instant somebody
    // is added and this chapter would turn green off a default nobody has
    // read. Hours are the one chapter whose answer arrives before the
    // question, so a recorded visit is the only honest evidence here - and
    // `visited` is persisted on the draft, so a resumed wizard still shows
    // the mark it had.
    hours: false,
    // NOT `hasCancellationWindow` any more, for exactly the reason `hours` is
    // `false` two lines up: the window OPENS on 24 hours (2026-08-20), so it
    // is true before the owner has seen the question and this chapter would
    // report itself answered on the wizard's first render, turning its card
    // from "not started" to "started, still owes something" before anybody
    // touched it. A default is not an answer.
    storefront:
      facts.phone.trim() !== '' || facts.hasPhoto || facts.hasDescription || facts.importContacts,
  };
  return CHAPTER_ORDER.filter((c) => answered[c]);
}

/** A chapter's mark on its card: nothing yet, green, or amber. */
export type ChapterMark = 'todo' | 'done' | 'warn';

/**
 * The MANDATORY items a chapter still owes, named with the server's own
 * readiness keys.
 *
 * These are a partition of `checkPublishReadiness`'s six keys
 * (`apps/api/src/trpc/business-profile.ts`) across the five chapters — the
 * client mirror in `business-onboarding.tsx` and this function must agree
 * with the server, because until submit there is no business to ask.
 *
 * The one key that needed splitting is `staff`. The server counts a person as
 * real only once they have at least one open day (a staff row with no hours
 * cannot take a booking), so that single key straddles two chapters here:
 * `team` owes it while nobody has been added at all, and `hours` owes it once
 * people exist but none of them work. Their conjunction is exactly the
 * server's rule, which `chapters.test.ts` asserts directly rather than
 * trusting this comment.
 */
export function chapterMissing(chapter: Chapter, facts: WizardFacts): readonly string[] {
  switch (chapter) {
    case 'identity':
      return [
        // STRICTER THAN THE SERVER, deliberately and in exactly one place
        // (owner, 2026-08-12: "each field is mandatory to proceed and get the
        // green check, for example the business name"). `checkPublishReadiness`
        // has no `name` key - a business row always has SOME name by the time
        // it exists - so nothing on the server can report this, and the client
        // is the only place it can be asked for. Being stricter is safe in the
        // one direction that matters: it can never publish something the
        // server would refuse. Do NOT extend this reasoning to `description`
        // or the contacts opt-in, which are optional on purpose - see
        // `chapters.test.ts`.
        ...(facts.bizName.trim() === '' ? ['name'] : []),
        // ONE entry however it fails - empty, or typed but never picked from
        // the list (see `hasAddressPin`). Both mean the same thing to the
        // server, which will have no address either way, so reporting two
        // would make the footer's "2 left" count an omission twice.
        ...(facts.address.trim() === '' || !facts.hasAddressPin ? ['address'] : []),
        ...(facts.categories.length === 0 ? ['category'] : []),
      ];
    case 'offering':
      return facts.serviceCount === 0 ? ['service'] : [];
    case 'team':
      return facts.staffCount === 0 ? ['staff'] : [];
    case 'hours':
      // Only owed once there is somebody to give hours TO. With nobody added,
      // `team` already carries the warning and repeating it here would mark
      // two cards amber for one omission.
      return facts.staffCount > 0 && facts.staffWithHoursCount === 0 ? ['staff'] : [];
    case 'storefront':
      return [
        ...(facts.phone.trim() === '' ? ['phone'] : []),
        // NO PHOTO HERE, since 2026-08-20, and it is the one rule in this file
        // that is deliberately LOOSER than the server's.
        //
        // A photo of the salon can only be taken IN the salon. Requiring one
        // to finish meant an owner signing up on the bus, or at home in the
        // evening, could not complete the wizard at all - it was the single
        // hardest item in the flow, and it gated everything behind it (owner,
        // 2026-08-20: "let them finish without it"). A business with no photo
        // now shows the ink-and-lotus default cover instead of a hole.
        //
        // `checkPublishReadiness` (apps/api/src/trpc/business-profile.ts) STILL
        // counts a photo, on purpose: finishing the wizard and appearing in the
        // marketplace are different bars. So an owner can answer every chapter,
        // submit, and still not be publishable - the business home page's
        // undismissable band is what asks for the photo, at the point where
        // they are likely to be standing in the room. If that server rule is
        // ever relaxed, this comment is the other half of the decision.
        // NOT one of the server's six readiness keys - it is the publish
        // gate's separate precondition. Reported here anyway because the
        // owner's test of "am I finished" is whether they can open for
        // business, and this is the last thing standing between approval and
        // going live.
        ...(facts.hasCancellationWindow ? [] : ['cancellationWindow']),
      ];
  }
}

/**
 * A chapter's mark, given what has been answered and which chapters the owner
 * has actually opened.
 *
 * VISITED, not "skipped": pressing `המשך` and pressing `דלג בינתיים` both
 * close the chapter, and with free order and no validation either can leave it
 * incomplete. So the mark is not about which button was pressed — it is
 * "you have been here, and this is what it still owes." A chapter never
 * visited stays blank rather than going amber, because an untouched chapter is
 * not a warning; it is just the next thing to do.
 */
export function chapterStatus(
  chapter: Chapter,
  facts: WizardFacts,
  visited: ReadonlySet<Chapter>,
): ChapterMark {
  // A chapter with nothing to answer yet can never be green, however long the
  // owner looked at it. Without this, an owner who opened `hours` before
  // adding anybody got a tick on an empty week: `hours` owes nothing while
  // there is no one to give hours TO (that omission is `team`'s to report),
  // and "owes nothing" would otherwise read as "done".
  if (!chapterAnswerable(chapter, facts)) return 'todo';
  if (chapterMissing(chapter, facts).length > 0) return visited.has(chapter) ? 'warn' : 'todo';
  return visited.has(chapter) ? 'done' : 'todo';
}

/**
 * Whether a chapter can be answered at all yet.
 *
 * Only `hours` can fail this, and only because working hours are per staff
 * member: with nobody added there is no week to set. It is deliberately NOT
 * modelled as a lock on the card — the chapter still opens, and it explains
 * itself once open. Free order means nothing is ever refused; this only stops
 * an empty chapter from claiming to be finished.
 */
export function chapterAnswerable(chapter: Chapter, facts: WizardFacts): boolean {
  return chapter !== 'hours' || facts.staffCount > 0;
}

/**
 * Whether `שליחה לאישור` may be pressed. Deliberately independent of
 * `visited`: an owner who filled everything in via a resumed draft has earned
 * submit without re-opening five sheets to prove it.
 */
export function allChaptersReady(facts: WizardFacts): boolean {
  return CHAPTER_ORDER.every((c) => chapterMissing(c, facts).length === 0);
}
